import { DocumentIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useState, useRef, ChangeEvent, useEffect } from 'react';

export interface DocumentUploadProps {
  accept?: string;
  maxSize?: number; // in MB
  description?: string;
  onConfirm: (file: File, displayName: string) => Promise<void>;
  isUploading?: boolean;
  className?: string;
  getFileIcon?: (fileType: string, fileName?: string) => React.ReactNode;
  formatFileSize?: (bytes: number) => string;
}

const isValidFileType = (file: File, accept: string): boolean => {
  if (accept === '*/*') return true;

  const allowedTypes = accept.split(',').map((type) => type.trim());
  const fileType = file.type;
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

  return allowedTypes.some((type) => {
    if (type.includes('*') && type.includes('/')) {
      return fileType.startsWith(type.replace('*', ''));
    } else if (type.startsWith('.')) {
      return fileExtension === type.toLowerCase();
    }
    return type === fileType;
  });
};

const defaultFormatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const defaultGetFileIcon = () => (
  <DocumentIcon className="size-6 text-slate-500" />
);

interface StagedFile {
  file: File;
  displayName: string;
}

export const DocumentUpload = ({
  accept = '*/*',
  maxSize = 100,
  description = 'PDF, Word, Excel, text, and images up to 100MB',
  onConfirm,
  isUploading = false,
  className = '',
  getFileIcon = defaultGetFileIcon,
  formatFileSize = defaultFormatFileSize,
}: DocumentUploadProps) => {
  const [stagedFile, setStagedFile] = useState<StagedFile | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Cleanup object URL to prevent memory leaks
  useEffect(() => {
    if (!stagedFile?.file || !stagedFile.file.type.startsWith('image/')) {
      setImagePreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(stagedFile.file);
    setImagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [stagedFile?.file]);

  const validateAndStageFile = (file: File) => {
    setError('');

    if (!isValidFileType(file, accept)) {
      setError('Invalid file type. Please upload an allowed file type.');
      return;
    }

    const maxSizeInBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      setError(`File size exceeds ${maxSize}MB limit`);
      return;
    }

    setStagedFile({
      file,
      displayName: '',
    });
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndStageFile(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndStageFile(e.dataTransfer.files[0]);
    }
  };

  const handleCancel = () => {
    setStagedFile(null);
    setError('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleConfirm = async () => {
    if (!stagedFile) return;

    await onConfirm(stagedFile.file, stagedFile.displayName.trim());
    setStagedFile(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      {stagedFile ? (
        // Staged file preview card
        <div className="animate-fadeIn rounded-xl border border-slate-200 bg-slate-50/50 p-4">
          <div className="flex items-start gap-3">
            {/* File icon or image preview */}
            <div className="shrink-0">
              {stagedFile.file.type.startsWith('image/') && imagePreviewUrl ? (
                <div className="size-12 overflow-hidden rounded-lg bg-slate-100">
                  <img
                    src={imagePreviewUrl}
                    alt="Preview"
                    className="size-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex size-12 items-center justify-center rounded-lg bg-slate-100">
                  {getFileIcon(stagedFile.file.type, stagedFile.file.name)}
                </div>
              )}
            </div>

            {/* File info and name input */}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {stagedFile.file.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {formatFileSize(stagedFile.file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-slate-200/50 hover:text-gray-600"
                  disabled={isUploading}
                >
                  <XMarkIcon className="size-4" />
                </button>
              </div>

              {/* Display name input */}
              <div className="mt-3">
                <input
                  type="text"
                  value={stagedFile.displayName}
                  onChange={(e) =>
                    setStagedFile((prev) =>
                      prev ? { ...prev, displayName: e.target.value } : null,
                    )
                  }
                  placeholder="Custom name (optional)"
                  className="focus:border-secondaryBlue focus:ring-secondaryBlue block w-full rounded-md border-slate-200 bg-white text-sm placeholder:text-gray-400"
                  disabled={isUploading}
                />
              </div>

              {/* Action buttons */}
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isUploading}
                  className="bg-secondaryBlue hover:bg-secondaryBlueShade focus:ring-secondaryBlue inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <svg
                        className="-ml-0.5 mr-1.5 size-3.5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isUploading}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Drop zone for selecting files
        <div>
          <div
            className={`relative flex justify-center rounded-xl border-2 border-dashed px-6 py-10 transition-all ${
              dragActive
                ? 'border-secondaryBlue bg-blue-50/50'
                : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50/50'
            } ${error ? 'border-red-500' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <DocumentIcon className="mx-auto size-10 text-slate-300" />
              <div className="mt-3 flex text-sm text-gray-600">
                <label
                  htmlFor="document-upload"
                  className="text-secondaryBlue hover:text-secondaryBlueShade relative cursor-pointer font-medium focus-within:outline-none"
                >
                  <span>Choose a file</span>
                  <input
                    ref={inputRef}
                    id="document-upload"
                    type="file"
                    accept={accept}
                    className="sr-only"
                    onChange={handleFileInputChange}
                    disabled={isUploading}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="mt-1 text-xs text-gray-500">{description}</p>
            </div>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};
